import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { CloudRain, Flame, HandshakeIcon, Cloud, Sunrise } from 'lucide-react';

const stages = [
  {
    id: 'denial',
    title: 'Denial',
    icon: CloudRain,
    color: 'from-gray-400 to-gray-500',
    description: 'The first reaction to loss',
    image: 'https://images.unsplash.com/photo-1618616191524-a9721186cbe4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjb25mdXNpb258ZW58MHx8fHwxNzMzOTI0MTczfDA&ixlib=rb-4.1.0&q=85',
    content: `Denial is often the first stage of grief. It's a defense mechanism that buffers the immediate shock of loss. We may find ourselves thinking "This can't be happening" or refusing to accept the reality of the situation. This temporary response helps us survive the initial wave of pain and gives us time to process the loss at our own pace.`,
    symptoms: [
      'Avoiding discussions about the loss',
      'Feeling numb or disconnected',
      'Pretending nothing has changed',
      'Difficulty accepting reality'
    ]
  },
  {
    id: 'anger',
    title: 'Anger',
    icon: Flame,
    color: 'from-orange-400 to-red-500',
    description: 'Masking pain with frustration',
    image: 'https://images.unsplash.com/photo-1604069961218-7d83bec96d7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxhbmdlcnxlbnwwfHx8fDE3MzM5MjQxNzN8MA&ixlib=rb-4.1.0&q=85',
    content: `As denial fades, reality and pain re-emerge. We're not ready to process this intense emotion, so it often gets redirected and expressed as anger. We might be angry at ourselves, at others, at medical professionals, or even at the person who left us. Anger is actually a sign of healing—it means we're starting to feel again.`,
    symptoms: [
      'Irritability and frustration',
      'Blaming others or circumstances',
      'Feeling abandoned or unfairly treated',
      'Increased sensitivity to minor issues'
    ]
  },
  {
    id: 'bargaining',
    title: 'Bargaining',
    icon: HandshakeIcon,
    color: 'from-yellow-400 to-amber-500',
    description: 'Seeking to regain control',
    image: 'https://images.unsplash.com/photo-1610894065081-fce3e6833dcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxob3BlfGVufDB8fHx8MTczMzkyNDE3M3ww&ixlib=rb-4.1.0&q=85',
    content: `In this stage, we may find ourselves dwelling on "what if" and "if only" statements. We might try to negotiate with a higher power, promising to change or do something in exchange for healing or return of what was lost. This is our way of trying to regain some control and postpone the inevitable reality of loss.`,
    symptoms: [
      'Ruminating on past actions',
      'Making deals with higher powers',
      'Focusing on what could have been done differently',
      'Seeking alternative explanations'
    ]
  },
  {
    id: 'depression',
    title: 'Depression',
    icon: Cloud,
    color: 'from-blue-400 to-blue-600',
    description: 'Deep sadness and reflection',
    image: 'https://images.unsplash.com/photo-1463385676620-a37609440d4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxzYWRuZXNzfGVufDB8fHx8MTczMzkyNDE3M3ww&ixlib=rb-4.1.0&q=85',
    content: `As we begin to live in the reality of our loss, the true magnitude of it becomes clear. This natural response to grief involves feelings of deep sadness, emptiness, and despair. We might withdraw from life, feel foggy and heavy, and wonder if life will ever feel normal again. This is not a sign of mental illness—it's an appropriate response to great loss.`,
    symptoms: [
      'Overwhelming sadness',
      'Withdrawal from social activities',
      'Changes in sleep or appetite',
      'Difficulty finding meaning or purpose'
    ]
  },
  {
    id: 'acceptance',
    title: 'Acceptance',
    icon: Sunrise,
    color: 'from-green-400 to-teal-500',
    description: 'Finding peace and moving forward',
    image: 'https://images.unsplash.com/photo-1499946981954-e7f4b234d7fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxwZWFjZXxlbnwwfHx8fDE3MzM5MjQxNzN8MA&ixlib=rb-4.1.0&q=85',
    content: `Acceptance is not about being "okay" with what happened—it's about acknowledging the reality and learning to live with it. We start to have more good days than bad, and we're able to remember our loss without being overwhelmed by pain. We find ways to carry our grief while also re-engaging with life and finding moments of peace and even joy.`,
    symptoms: [
      'Accepting the new reality',
      'Re-engaging with life and relationships',
      'Finding meaning in the experience',
      'Moments of peace and gratitude'
    ]
  }
];

const GriefStages = () => {
  return (
    <section id="stages" className="py-20 bg-gradient-to-b from-white to-blue-50 relative">
      {/* Background decorative image */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <img 
          src="https://images.unsplash.com/photo-1566044366987-6de6ca566441?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxwZWFjZWZ1bHxlbnwwfHx8fDE3NzMzOTE2MjF8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            The Five Stages of Grief
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Developed by Elisabeth Kübler-Ross, these stages are not linear. You may experience them 
            in any order, revisit them multiple times, or skip some entirely. Everyone grieves differently.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {stages.map((stage, index) => {
              const Icon = stage.icon;
              return (
                <AccordionItem 
                  key={stage.id} 
                  value={stage.id}
                  className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <AccordionTrigger className="px-6 py-5 hover:no-underline group">
                    <div className="flex items-center gap-4 w-full">
                      <div className={`p-3 rounded-lg bg-gradient-to-br ${stage.color} text-white group-hover:scale-110 transition-transform duration-200`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-400">Stage {index + 1}</span>
                          <h3 className="text-2xl font-bold text-gray-900">{stage.title}</h3>
                        </div>
                        <p className="text-gray-600 mt-1">{stage.description}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    {/* Stage Image */}
                    <div className="relative h-64 rounded-lg overflow-hidden mb-4">
                      <img 
                        src={stage.image}
                        alt={`${stage.title} stage of grief`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>
                    
                    <div className="pt-4 space-y-4">
                      <p className="text-gray-700 leading-relaxed">
                        {stage.content}
                      </p>
                      <div className="bg-blue-50 rounded-lg p-4 mt-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Common Signs & Symptoms:</h4>
                        <ul className="space-y-2">
                          {stage.symptoms.map((symptom, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <span className="text-blue-600 mt-1">•</span>
                              <span>{symptom}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="mt-12 max-w-4xl mx-auto bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8 border border-blue-200">
          <p className="text-gray-700 leading-relaxed text-center">
            <strong>Remember:</strong> Grief is not a linear process. You might move back and forth between stages, 
            experience multiple stages simultaneously, or find that some stages don't resonate with your experience. 
            There's no "right" way to grieve, and no timeline for healing.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GriefStages;
